import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ArrowLeft, Download, Send, Mail } from "lucide-react";
import { api, apiError, API, fmtDate } from "../../lib/api";
import { localeOf } from "../../i18n";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";

const Metric = ({ label, value, testId }) => (
  <div className="vy-card p-4" data-testid={testId}>
    <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">{label}</p>
    <p className="mt-1 font-display text-2xl font-extrabold tabular-nums">{value}</p>
  </div>
);

export default function PartnerReport() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const locale = localeOf(i18n.language);
  const { user } = useAuth();
  const isAdmin = ["admin", "super_admin"].includes(user?.role);
  const lang = i18n.language.slice(0, 2);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const [to, setTo] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/events/${id}/partner-report?lang=${lang}`).then(({ data }) => {
    setReport(data);
    setError(null);
    setTo(data.partner?.contact_email || "");
  });
  useEffect(() => {
    let cancelled = false;
    load().catch((e) => { if (!cancelled) setError(e?.response?.status === 403 ? "forbidden" : "error"); });
    return () => { cancelled = true; };
  }, [id, lang]);

  const send = async () => {
    setBusy(true);
    try {
      await api.post(`/events/${id}/partner-report/send`, { to_email: to, lang, message });
      toast.success(t("report.sent"));
      setOpen(false); setMessage(""); load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail === "email_quota_exceeded") toast.error(t("report.quotaExceeded"));
      else toast.error(apiError(e));
    } finally { setBusy(false); }
  };

  if (error) {
    return (
      <div className="space-y-4" data-testid="report-error">
        <Link to={`/staff/events/${id}`} data-testid="report-back-link"
          className="inline-flex items-center gap-2 text-sm font-bold text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" />{t("common.back")}
        </Link>
        <div className="vy-card p-6">
          <h1 className="font-display text-xl font-bold">{t("report.title")}</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {error === "forbidden" ? t("report.forbidden") : t("report.loadError")}
          </p>
        </div>
      </div>
    );
  }
  if (!report) return <p className="text-sm text-muted-foreground">{t("common.loading")}</p>;
  const m = report.metrics;

  return (
    <div className="space-y-6" data-testid="partner-report-page">
      <Link to={`/staff/events/${id}`} data-testid="report-back-link"
        className="inline-flex items-center gap-2 text-sm font-bold text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" />{t("common.back")}
      </Link>

      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-wide text-accent">{t("report.title")}</p>
          <h1 className="font-display text-3xl font-extrabold" data-testid="report-event-title">
            {report.event.title}
          </h1>
          <p className="text-sm text-muted-foreground">
            {fmtDate(report.event.starts_at, locale)}
            {report.event.location ? ` · ${report.event.location}` : ""}
            {report.partner?.name ? ` · ${report.partner.name}` : ""}
            {report.campaign?.name ? ` · ${report.campaign.name}` : ""}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" data-testid="report-pdf-btn"
            onClick={() => window.open(`${API}/events/${id}/partner-report.pdf?lang=${lang}`, "_blank")}
            className="h-11 rounded-full font-bold">
            <Download className="mr-2 h-4 w-4" />{t("report.pdf")}
          </Button>
          {isAdmin && (
            <Button data-testid="report-send-btn" onClick={() => setOpen(true)}
              className="h-11 rounded-full font-bold">
              <Send className="mr-2 h-4 w-4" />{t("report.reviewSend")}
            </Button>
          )}
        </div>
      </header>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Metric label={t("report.ambassadors")} value={m.ambassadors_mobilised} testId="report-ambassadors" />
        <Metric label={t("analytics.rsvps")} value={m.rsvps} testId="report-rsvps" />
        <Metric label={t("events.checkedIn")} value={m.checked_in} testId="report-checkins" />
        <Metric label={t("analytics.attendanceRate")} value={`${m.attendance_rate}%`} testId="report-attendance" />
        <Metric label={t("report.leads")} value={m.leads} testId="report-leads" />
        <Metric label={t("report.missions")} value={`${m.missions_completed}/${m.missions_total}`} testId="report-missions" />
        <Metric label={t("report.content")} value={m.photos} testId="report-content" />
        <Metric label={t("home.pending")} value={m.proofs_pending} testId="report-pending" />
      </div>

      <section className="vy-card p-5" data-testid="report-summary">
        <h2 className="font-display text-lg font-bold">{t("report.summary")}</h2>
        <ul className="mt-2 space-y-2">
          {report.summary.map((s, i) => (
            <li key={i} className="text-sm text-muted-foreground">{s}</li>
          ))}
        </ul>
        {report.event.objectives && (
          <p className="mt-4 border-t border-border pt-4 text-sm">
            <span className="font-bold">{t("events.objectives")} : </span>{report.event.objectives}
          </p>
        )}
      </section>

      <div className="grid gap-4 lg:grid-cols-2">
        <section className="vy-card p-5" data-testid="report-ambassadors-table">
          <h2 className="font-display text-lg font-bold">{t("report.ambassadors")}</h2>
          <table className="mt-3 w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase text-muted-foreground">
                <th className="py-2">{t("admin.name")}</th>
                <th className="py-2">{t("events.checkedIn")}</th>
                <th className="py-2">{t("report.missions")}</th>
                <th className="py-2">{t("report.leads")}</th>
              </tr>
            </thead>
            <tbody>
              {report.ambassadors.map((a, i) => (
                <tr key={i} className="border-t border-border">
                  <td className="py-2.5 font-semibold">{a.name}</td>
                  <td className="py-2.5">{a.checked_in ? "✓" : "—"}</td>
                  <td className="py-2.5 tabular-nums">{a.missions_approved}</td>
                  <td className="py-2.5 tabular-nums">{a.leads}</td>
                </tr>
              ))}
              {report.ambassadors.length === 0 && (
                <tr><td colSpan={4} className="py-3 text-muted-foreground">{t("common.none")}</td></tr>
              )}
            </tbody>
          </table>
        </section>

        <section className="vy-card p-5" data-testid="report-missions-table">
          <h2 className="font-display text-lg font-bold">{t("report.missions")}</h2>
          <ul className="mt-3 divide-y divide-border">
            {report.missions_breakdown.map((r, i) => (
              <li key={i} className="flex items-center justify-between py-2.5 text-sm">
                <span className="font-semibold">{r.title}</span>
                <span className="tabular-nums">{r.completed}</span>
              </li>
            ))}
            {report.missions_breakdown.length === 0 && (
              <li className="py-3 text-sm text-muted-foreground">{t("common.none")}</li>
            )}
          </ul>
          <h2 className="mt-6 font-display text-lg font-bold">{t("report.leadsDelivery")}</h2>
          <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
            {Object.entries(report.leads_delivery).map(([k, v]) => (
              <li key={k}>{t(`outbox.status.${k}`, k)} · {v}</li>
            ))}
            {Object.keys(report.leads_delivery).length === 0 && <li>{t("common.none")}</li>}
          </ul>
        </section>
      </div>

      {report.last_send && (
        <p className="text-xs text-muted-foreground" data-testid="report-last-send">
          <Mail className="mr-1 inline h-3.5 w-3.5" />
          {t("report.lastSend", {
            email: report.last_send.to_email,
            date: fmtDate(report.last_send.sent_at, locale),
          })}
        </p>
      )}
      {report.last_attempt && (
        <p className="text-xs text-destructive" data-testid="report-last-attempt">
          <Mail className="mr-1 inline h-3.5 w-3.5" />
          {t("report.lastAttemptFailed", {
            email: report.last_attempt.to_email,
            date: fmtDate(report.last_attempt.sent_at, locale),
          })}
        </p>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="report-send-dialog">
          <DialogHeader><DialogTitle>{t("report.reviewSend")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">{t("report.reviewSendHint")}</p>
            <Input data-testid="report-send-email" className="h-12" type="email"
              placeholder={t("auth.email")} value={to} onChange={(e) => setTo(e.target.value)} />
            <Textarea data-testid="report-send-message" rows={3} placeholder={t("report.messageOptional")}
              value={message} onChange={(e) => setMessage(e.target.value)} />
          </div>
          <DialogFooter>
            <Button data-testid="report-send-confirm" onClick={send} disabled={busy || !to}
              className="h-12 rounded-full font-bold">
              {busy ? t("common.sending") : t("report.confirmSend")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
